import {useState} from 'react'
import {Link, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter} from '@/components/ui/dialog'
import {PageHeading} from '@/components/page-heading'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {FiltrosDeEstado, type OpcionEstado} from '@/components/ui/filtros-de-estado'
import {NoAccessState} from '@/components/no-access-state'
import {ListEmptyState} from '@/components/section-card'
import {IncapacidadRow} from '@/components/employee-incapacidades-section'
import {
  INCAPACIDADES_PAGE_SIZE,
  useIncapacidadesQueue,
  type Incapacidad,
} from '@/hooks/empresa/mi_espacio/use-incapacidades'
import {useBusiness, useCapabilities, useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {
  SUGERIDO_KIND_LABEL_KEY,
  useConfirmarPendientes,
  usePendientes,
  useReconfirmacion,
  useSoportesPorVencer,
  PENDIENTES_PAGE_SIZE,
  type SoporteVencimiento,
  type SoporteVencimientoFiltro,
  type Sugerencia,
} from '@/hooks/sgsst/evaluacion/use-sgsst-pendientes'
import {formatDate, formatDateOnly} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'

function EmptyState({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  return (
    <div className="surface" data-testid="pendientes-empty">
      <ListEmptyState
        page
        title={t('marcado.pendientes.empty.title')}
        action={
          <Button asChild size="sm" variant="outline">
            <Link to={`/businesses/${businessId}/sgsst`}>{t('marcado.pendientes.empty.backToChecklist')}</Link>
          </Button>
        }
      />
    </div>
  )
}

function SectionHead({
  title,
  criterio,
  criterioLabel,
  testId,
}: {
  title: string
  criterio?: string
  criterioLabel?: string
  testId?: string
}) {
  return (
    <div className="surface-divider flex items-center gap-1.5 border-b px-4 py-3">
      <h2 className="font-heading text-base text-foreground">{title}</h2>
      {criterio && (
        <HelpIcon label={criterioLabel!} testId={testId}>
          {criterio}
        </HelpIcon>
      )}
    </div>
  )
}

type VencimientoFiltro = SoporteVencimientoFiltro | 'todos'

/** Cola de renovación (spec soportes esperados §4): orientativa, nada aquí bloquea. */
function SoportesVencimientosSection({
  businessId,
  rows,
  total,
  page,
  onPage,
  estado,
  onEstado,
  conteoVencido,
  conteoVencePronto,
}: {
  businessId: string
  rows: SoporteVencimiento[]
  total: number
  page: number
  onPage: (page: number) => void
  estado: VencimientoFiltro
  onEstado: (estado: VencimientoFiltro) => void
  conteoVencido: number
  conteoVencePronto: number
}) {
  const {t} = useTranslation()
  if (conteoVencido + conteoVencePronto === 0) return null
  const opciones: OpcionEstado<VencimientoFiltro>[] = [
    {
      valor: 'todos',
      etiqueta: t('marcado.pendientes.soportes.filtro.todos'),
      conteo: conteoVencido + conteoVencePronto,
    },
    {valor: 'vencido', etiqueta: t('evidence.soportes.estado.vencido'), conteo: conteoVencido},
    {valor: 'vence_pronto', etiqueta: t('evidence.soportes.estado.vence_pronto'), conteo: conteoVencePronto},
  ]
  return (
    <section className="surface overflow-hidden" data-testid="soportes-vencimientos-section">
      <SectionHead
        title={t('marcado.pendientes.soportes.eyebrow')}
        criterio={t('marcado.pendientes.soportes.subtitle')}
        criterioLabel={t('marcado.pendientes.soportes.tipLabel')}
        testId="soportes-vencimientos-tip"
      />
      <div className="px-4 pt-3">
        <FiltrosDeEstado
          opciones={opciones}
          valor={estado}
          onValor={onEstado}
          testIdPrefijo="soportes-vencimientos-filtro"
        />
      </div>
      <ul className="px-4 pb-1.5">
        {rows.map((row) => (
          <li
            key={row.evidence_id}
            className="-mx-4 flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-secondary/30"
            data-testid="soporte-vencimiento-row">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-foreground">
                <span className="font-mono text-xs text-muted-foreground">{row.standard_code}</span>{' '}
                {row.soporte_titulo ?? row.standard_name}
              </p>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {row.filename}
                {' · '}
                <span className="font-mono">
                  {t(row.estado === 'vencido' ? 'evidence.soportes.vencioEl' : 'evidence.soportes.venceEl', {
                    date: formatDateOnly(row.vence_el),
                  })}
                </span>
              </p>
            </div>
            <Tag tone={row.estado === 'vencido' ? 'red' : 'amber'}>{t(`evidence.soportes.estado.${row.estado}`)}</Tag>
            <Button asChild size="sm" variant="outline">
              <Link to={`/businesses/${businessId}/sgsst#std-${row.standard_code}`}>
                {t('marcado.pendientes.soportes.open')}
              </Link>
            </Button>
          </li>
        ))}
      </ul>
      <div className="px-4 pb-3">
        <PaginationBar page={page} pageSize={PENDIENTES_PAGE_SIZE} total={total} onPageChange={onPage} />
      </div>
    </section>
  )
}

/** Va encima de los vencimientos: un dato de salud sin validar es más urgente (spec incapacidades §3). */
function IncapacidadesPendientesSection({
  businessId,
  rows,
  total,
  page,
  onPage,
}: {
  businessId: string
  rows: Incapacidad[]
  total: number
  page: number
  onPage: (page: number) => void
}) {
  const {t} = useTranslation()
  if (total === 0) return null
  return (
    <section className="surface overflow-hidden" data-testid="incapacidades-pendientes-section">
      <SectionHead
        title={t('marcado.pendientes.incapacidades.eyebrow', {count: total})}
        criterio={t('marcado.pendientes.incapacidades.subtitle')}
        criterioLabel={t('marcado.pendientes.incapacidades.tipLabel')}
        testId="incapacidades-pendientes-tip"
      />
      <ul className="px-4 pb-1.5">
        {rows.map((incapacidad) => (
          <IncapacidadRow key={incapacidad.id} businessId={businessId} incapacidad={incapacidad} canOperate />
        ))}
      </ul>
      <div className="px-4 pb-3">
        <PaginationBar page={page} pageSize={INCAPACIDADES_PAGE_SIZE} total={total} onPageChange={onPage} />
      </div>
    </section>
  )
}

function SugerenciaRow({
  row,
  selected,
  onToggle,
  onConfirmOne,
  pending,
  godModeBlocked,
  disenoBlockedReason,
  reconfirmacion = false,
}: {
  row: Sugerencia
  selected: boolean
  onToggle: () => void
  onConfirmOne: () => void
  pending: boolean
  godModeBlocked: boolean
  // Razón de capacidad (identity-access-spec §1a.3) para cuando la fila no trae `confirm_blocked_reason`.
  disenoBlockedReason: string | null
  // Mismo acto que una primera confirmación; solo cambian los testids y la etiqueta.
  reconfirmacion?: boolean
}) {
  const {t} = useTranslation()
  const disabled = !row.can_confirm || godModeBlocked
  const tooltip = godModeBlocked
    ? t('marcado.godMode.tooltip')
    : !row.can_confirm
      ? (disenoBlockedReason ?? t('marcado.pendientes.blockedTag'))
      : undefined
  return (
    <li
      className="-mx-4 flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-secondary/30"
      data-testid={reconfirmacion ? 'reconfirmacion-row' : 'pendiente-row'}>
      <input
        type="checkbox"
        aria-label={t('marcado.pendientes.selectRow', {code: row.standard_code})}
        checked={selected}
        disabled={disabled}
        onChange={onToggle}
        className="mt-0.5 accent-primary disabled:opacity-40"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground">
          <span className="font-mono text-xs text-muted-foreground">{row.standard_code}</span> {row.standard_name}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('marcado.pendientes.backedBy', {artifact: t(SUGERIDO_KIND_LABEL_KEY[row.backed_by_kind])})}
          {row.backed_by_version && ` · ${row.backed_by_version}`}
          {' · '}
          {formatDate(row.computed_at)}
        </p>
        {reconfirmacion && (
          <Tag tone="amber" className="mt-1" data-testid="reconfirmacion-tag">
            {t('marcado.pendientes.reconfirmacion.tag')}
          </Tag>
        )}
        {!row.can_confirm && (
          <Tag tone="amber" className="mt-1">
            {t('marcado.pendientes.blockedTag')}
          </Tag>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || pending}
        title={tooltip}
        onClick={onConfirmOne}
        data-testid={reconfirmacion ? 'reconfirmacion-confirm-one' : 'pendiente-confirm-one'}>
        {t(reconfirmacion ? 'marcado.pendientes.reconfirmacion.confirmOne' : 'marcado.pendientes.confirmOne')}
      </Button>
    </li>
  )
}

const TABS = ['marcado', 'incapacidades', 'vencimientos'] as const
type PendientesTab = (typeof TABS)[number]

type MarcadoFiltro = 'todos' | 'sugeridos' | 'reconfirmacion'

function ChipConteo({n}: {n: number}) {
  if (n === 0) return null
  return <span className="ml-1.5 rounded-full bg-secondary px-1.5 font-mono text-[10px] text-foreground">{n}</span>
}

export function SgsstPendientesPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {data: business, isLoading: businessLoading} = useBusiness(businessId)
  const isSystemUser = useIsSystemUser()
  const capabilities = useCapabilities(businessId)
  const canAccess = capabilities.sgsst_operar
  // Confirmar un sugerido es acto de diseño (la matriz de riesgos, arts. 4/10/17): lee la puerta de
  // diseño, no la de firma del art. 27.
  const canDisenar = capabilities.sgsst_disenar
  const disenoBlockedReason = capabilities.disenar_bloqueado_por
  // Solo dev: el usuario de sistema confirma para E2E y `disenoBlockedReason` sigue intacto. En prod
  // `dev_unlicensed_override` está apagado y `sgsst_dev_override` siempre es false.
  const godModeBlocked = isSystemUser && !capabilities.sgsst_dev_override
  const batchBlocked = godModeBlocked || (!isSystemUser && !canDisenar)
  const [sugeridosPage, setSugeridosPage] = useState(1)
  const [reconfirmacionPage, setReconfirmacionPage] = useState(1)
  const [soportesPage, setSoportesPage] = useState(1)
  const [incapacidadesPageNum, setIncapacidadesPageNum] = useState(1)
  // Cada chip vuelve a la página 1 al cambiar: es contrato del llamador de `FiltrosDeEstado`.
  const [marcadoFiltro, setMarcadoFiltro] = useState<MarcadoFiltro>('todos')
  const [vencimientoFiltro, setVencimientoFiltro] = useState<VencimientoFiltro>('todos')
  const {data: page, isLoading} = usePendientes(canAccess ? businessId : undefined, sugeridosPage)
  const {data: reconfirmacionData, isLoading: reconfirmacionLoading} = useReconfirmacion(
    canAccess ? businessId : undefined,
    reconfirmacionPage
  )
  const {data: soportesData, isLoading: soportesLoading} = useSoportesPorVencer(
    canAccess ? businessId : undefined,
    soportesPage,
    vencimientoFiltro === 'todos' ? undefined : vencimientoFiltro
  )
  const {data: incapacidadesPage, isLoading: incapacidadesLoading} = useIncapacidadesQueue(
    canAccess ? businessId : undefined,
    'reportada',
    incapacidadesPageNum
  )
  const confirmar = useConfirmarPendientes(businessId!)
  // Guarda la fila y no solo su id: la selección sobrevive al cambio de página, y el diálogo, el
  // conteo del primario y el POST deben hablar del mismo conjunto.
  const [selected, setSelected] = useState<Map<string, Sugerencia>>(new Map())
  const [reviewing, setReviewing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  if (businessLoading) return <Skeleton className="h-60 w-full" />
  if (!business || !canAccess) return <NoAccessState />
  if (
    isLoading ||
    reconfirmacionLoading ||
    soportesLoading ||
    incapacidadesLoading ||
    !page ||
    !reconfirmacionData ||
    !soportesData ||
    !incapacidadesPage
  )
    return <Skeleton className="h-60 w-full" />

  const rows = page.items
  const reconfirmacionRows = reconfirmacionData.items
  const soportesRows = soportesData.items
  const incapacidadesRows = incapacidadesPage.items
  const confirmableRows = rows.filter((r) => r.can_confirm)
  const marcadoTotal = page.total + reconfirmacionData.total
  // Cuenta el negocio y no el filtro: con un chip puesto `soportesData.total` es el de la página filtrada.
  const soportesTotal = soportesData.conteo_vencido + soportesData.conteo_vence_pronto
  const tab: PendientesTab = TABS.includes(searchParams.get('tab') as PendientesTab)
    ? (searchParams.get('tab') as PendientesTab)
    : 'marcado'
  function elegirMarcado(next: MarcadoFiltro) {
    setMarcadoFiltro(next)
    setSugeridosPage(1)
    setReconfirmacionPage(1)
  }

  function elegirVencimiento(next: VencimientoFiltro) {
    setVencimientoFiltro(next)
    setSoportesPage(1)
  }

  function setTab(next: PendientesTab) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  function toggle(row: Sugerencia) {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(row.id)) next.delete(row.id)
      else next.set(row.id, row)
      return next
    })
  }

  function submitIds(ids: string[]) {
    setError(null)
    confirmar.mutate(ids, {
      onSuccess: () => {
        setSelected(new Map())
        setReviewing(false)
      },
      onError: (err) => setError(translateApiError(err)),
    })
  }

  const selectedRows = [...selected.values()]

  return (
    <div className="space-y-5" data-testid="pendientes-page">
      {/* El motivo del apagado va a un HelpIcon y no a `title=`: `disabled:pointer-events-none` impide el
          hover y `title` no abre por teclado ni por toque (U6/U15). */}
      <PageHeading
        title={t('marcado.pendientes.title')}
        accion={
          tab === 'marcado' &&
          selected.size > 0 && (
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                disabled={batchBlocked}
                onClick={() => setReviewing(true)}
                data-testid="pendientes-confirm-batch">
                {t('marcado.pendientes.confirmSelected', {count: selectedRows.length})}
              </Button>
              {batchBlocked && (
                <HelpIcon label={t('marcado.pendientes.bloqueoLabel')} testId="pendientes-confirm-bloqueo">
                  {godModeBlocked
                    ? t('marcado.godMode.tooltip')
                    : (disenoBlockedReason ?? t('marcado.pendientes.blockedTag'))}
                </HelpIcon>
              )}
            </div>
          )
        }
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      {marcadoTotal === 0 && soportesTotal === 0 && incapacidadesPage.total === 0 ? (
        <EmptyState businessId={businessId!} />
      ) : (
        <Tabs value={tab} onValueChange={(v) => setTab(v as PendientesTab)}>
          <TabsList data-testid="pendientes-tabs">
            <TabsTrigger value="marcado" data-testid="pendientes-tab-marcado">
              {t('marcado.pendientes.tabs.marcado')}
              <ChipConteo n={marcadoTotal} />
            </TabsTrigger>
            <TabsTrigger value="incapacidades" data-testid="pendientes-tab-incapacidades">
              {t('marcado.pendientes.tabs.incapacidades')}
              <ChipConteo n={incapacidadesPage.total} />
            </TabsTrigger>
            <TabsTrigger value="vencimientos" data-testid="pendientes-tab-vencimientos">
              {t('marcado.pendientes.tabs.vencimientos')}
              <ChipConteo n={soportesTotal} />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="marcado" className="space-y-5 pt-4">
            {marcadoTotal === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="pendientes-marcado-vacio">
                {t('marcado.pendientes.tabs.marcadoVacio')}
              </p>
            )}
            {marcadoTotal > 0 && reconfirmacionData.total > 0 && (
              <FiltrosDeEstado
                opciones={[
                  {valor: 'todos', etiqueta: t('marcado.pendientes.filtro.todos'), conteo: marcadoTotal},
                  {valor: 'sugeridos', etiqueta: t('marcado.pendientes.filtro.sugeridos'), conteo: page.total},
                  {
                    valor: 'reconfirmacion',
                    etiqueta: t('marcado.pendientes.filtro.reconfirmacion'),
                    conteo: reconfirmacionData.total,
                  },
                ]}
                valor={marcadoFiltro}
                onValor={elegirMarcado}
                testIdPrefijo="pendientes-marcado-filtro"
              />
            )}
            {marcadoFiltro !== 'sugeridos' && reconfirmacionData.total > 0 && (
              <section className="surface overflow-hidden" data-testid="reconfirmacion-section">
                <SectionHead
                  title={t('marcado.pendientes.reconfirmacion.listEyebrow')}
                  criterio={t('marcado.pendientes.reconfirmacion.subtitle')}
                  criterioLabel={t('marcado.pendientes.reconfirmacion.tipLabel')}
                  testId="reconfirmacion-tip"
                />
                <ul className="px-4 pb-1.5">
                  {reconfirmacionRows.map((row) => (
                    <SugerenciaRow
                      key={row.id}
                      row={row}
                      reconfirmacion
                      selected={selected.has(row.id)}
                      onToggle={() => toggle(row)}
                      onConfirmOne={() => submitIds([row.id])}
                      pending={confirmar.isPending}
                      godModeBlocked={godModeBlocked}
                      disenoBlockedReason={disenoBlockedReason}
                    />
                  ))}
                </ul>
                <div className="px-4 pb-3">
                  <PaginationBar
                    page={reconfirmacionPage}
                    pageSize={PENDIENTES_PAGE_SIZE}
                    total={reconfirmacionData.total}
                    onPageChange={setReconfirmacionPage}
                  />
                </div>
              </section>
            )}

            {marcadoFiltro !== 'reconfirmacion' && page.total > 0 && (
              <section className="surface overflow-hidden">
                <SectionHead title={t('marcado.pendientes.listEyebrow')} />
                <ul className="px-4">
                  {rows.map((row) => (
                    <SugerenciaRow
                      key={row.id}
                      row={row}
                      selected={selected.has(row.id)}
                      onToggle={() => toggle(row)}
                      onConfirmOne={() => submitIds([row.id])}
                      pending={confirmar.isPending}
                      godModeBlocked={godModeBlocked}
                      disenoBlockedReason={disenoBlockedReason}
                    />
                  ))}
                </ul>
                {confirmableRows.length === 0 && rows.length > 0 && (
                  <p className="px-4 py-3 text-xs text-muted-foreground" data-testid="pendientes-none-actionable">
                    {t('marcado.pendientes.noneActionable')}
                  </p>
                )}
                <div className="px-4 pb-3">
                  <PaginationBar
                    page={sugeridosPage}
                    pageSize={PENDIENTES_PAGE_SIZE}
                    total={page.total}
                    onPageChange={setSugeridosPage}
                  />
                </div>
              </section>
            )}
          </TabsContent>

          <TabsContent value="incapacidades" className="pt-4">
            {incapacidadesPage.total === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="pendientes-incapacidades-vacio">
                {t('marcado.pendientes.tabs.incapacidadesVacio')}
              </p>
            ) : (
              <IncapacidadesPendientesSection
                businessId={businessId!}
                rows={incapacidadesRows}
                total={incapacidadesPage.total}
                page={incapacidadesPageNum}
                onPage={setIncapacidadesPageNum}
              />
            )}
          </TabsContent>

          <TabsContent value="vencimientos" className="pt-4">
            {soportesTotal === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="pendientes-vencimientos-vacio">
                {t('marcado.pendientes.tabs.vencimientosVacio')}
              </p>
            ) : (
              <SoportesVencimientosSection
                businessId={businessId!}
                rows={soportesRows}
                total={soportesData.total}
                page={soportesPage}
                onPage={setSoportesPage}
                estado={vencimientoFiltro}
                onEstado={elegirVencimiento}
                conteoVencido={soportesData.conteo_vencido}
                conteoVencePronto={soportesData.conteo_vence_pronto}
              />
            )}
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={reviewing} onOpenChange={setReviewing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('marcado.pendientes.reviewTitle', {count: selectedRows.length})}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('marcado.pendientes.reviewBody')}</p>
          <ul className="max-h-60 space-y-2 overflow-y-auto rounded-lg bg-secondary/50 p-3">
            {selectedRows.map((row) => (
              <li key={row.id} className="text-sm">
                <span className="font-mono text-xs text-muted-foreground">{row.standard_code}</span> {row.standard_name}
                <span className={cn('block text-xs text-muted-foreground')}>
                  {t(SUGERIDO_KIND_LABEL_KEY[row.backed_by_kind])}
                </span>
              </li>
            ))}
          </ul>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setReviewing(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={confirmar.isPending}
              onClick={() => submitIds(selectedRows.map((r) => r.id))}
              data-testid="pendientes-confirm-batch-submit">
              {t('marcado.pendientes.reviewConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
